import React, { useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { toggleVobAssignmentBulkImportModal } from "../../../../app/slices/ProfileModuleSlice";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    Alert,
    Box,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useBulkImportVobAssignmentsMutation } from "../../../../services/profileService/profile-api";
import { BulkImportAssignmentDto, VobAssignmentBulkImportResponse } from "../../../../services/profileService/commands";

interface ParsedRow extends BulkImportAssignmentDto {
    lineNumber: number;
    isValid: boolean;
    validationError?: string;
}

function VobAssignmentBulkImport() {
    const modalOpen = useAppSelector((state) => state.profileModule.vobAssignmentBulkImportModal);
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [bulkImport, { isLoading }] = useBulkImportVobAssignmentsMutation();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
    const [parseError, setParseError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<VobAssignmentBulkImportResponse | null>(null);

    const validAssignmentTypes = ['Center', 'Payer'];
    const validProcessTypes = ['Verifications', 'Authorizations'];

    const validateRow = (row: Omit<ParsedRow, 'isValid' | 'validationError'>): { isValid: boolean; error?: string } => {
        if (!row.upn || row.upn.trim() === '') {
            return { isValid: false, error: 'UPN is required' };
        }
        if (!row.assignmentValue || row.assignmentValue.trim() === '') {
            return { isValid: false, error: 'Assignment Value is required' };
        }
        if (!validAssignmentTypes.includes(row.assignmentType)) {
            return { isValid: false, error: `Invalid Assignment Type. Must be: ${validAssignmentTypes.join(', ')}` };
        }
        if (!validProcessTypes.includes(row.processType)) {
            return { isValid: false, error: `Invalid Process Type. Must be: ${validProcessTypes.join(', ')}` };
        }
        return { isValid: true };
    };

    const parseCsvLine = (line: string): string[] => {
        const result: string[] = [];
        let currentValue = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const c = line[i];
            if (c === '"') {
                if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
                    currentValue += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (c === ',' && !inQuotes) {
                result.push(currentValue);
                currentValue = '';
            } else {
                currentValue += c;
            }
        }
        result.push(currentValue);
        return result;
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedFile(file);
        setParseError(null);
        setImportResult(null);
        setParsedData([]);

        if (!file.name.endsWith('.csv')) {
            setParseError('Please select a CSV file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                const lines = text.split('\n').filter(line => line.trim() !== '');

                if (lines.length < 2) {
                    setParseError('CSV file must have a header row and at least one data row');
                    return;
                }

                const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                const expectedHeaders = ['upn', 'assignmentvalue', 'assignmenttype', 'processtype'];

                if (!expectedHeaders.every((h, i) => headers[i] === h)) {
                    setParseError(`Invalid CSV header. Expected: UPN,AssignmentValue,AssignmentType,ProcessType`);
                    return;
                }

                const parsed: ParsedRow[] = [];
                for (let i = 1; i < lines.length; i++) {
                    const values = parseCsvLine(lines[i]);
                    if (values.length < 4) continue;

                    const row: Omit<ParsedRow, 'isValid' | 'validationError'> = {
                        lineNumber: i + 1,
                        upn: values[0].trim(),
                        assignmentValue: values[1].trim(),
                        assignmentType: values[2].trim(),
                        processType: values[3].trim()
                    };

                    const validation = validateRow(row);
                    parsed.push({
                        ...row,
                        isValid: validation.isValid,
                        validationError: validation.error
                    });
                }

                setParsedData(parsed);
            } catch (err) {
                setParseError('Error parsing CSV file');
            }
        };
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const result = await bulkImport(formData).unwrap();
            setImportResult(result);
        } catch (error: any) {
            setImportResult({
                success: false,
                totalProcessed: 0,
                successCount: 0,
                errorCount: 1,
                errors: [error?.data?.errors?.[0] || error?.message || 'Import failed'],
                processedProfiles: []
            });
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setParsedData([]);
        setParseError(null);
        setImportResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        dispatch(toggleVobAssignmentBulkImportModal());
    };

    const handleDownloadTemplate = () => {
        const template = `UPN,AssignmentValue,AssignmentType,ProcessType
user1@domain.com,H01,Center,Verifications
user1@domain.com,MBM,Center,Verifications
user2@domain.com,Commercial,Payer,Authorizations`;

        const blob = new Blob([template], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'vob_assignments_template.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const validRows = parsedData.filter(r => r.isValid);
    const invalidRows = parsedData.filter(r => !r.isValid);

    return (
        <Dialog open={modalOpen} maxWidth="lg" fullWidth onClose={handleClose}>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Bulk Import VOB Assignments</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleDownloadTemplate}
                        startIcon={<FontAwesomeIcon icon="download" />}
                    >
                        Download Template
                    </Button>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* File Upload Section */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            CSV Format: UPN, AssignmentValue, AssignmentType, ProcessType
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            • AssignmentType: Center or Payer
                            <br />
                            • ProcessType: Verifications or Authorizations
                            <br />
                            • AssignmentValue: Center code (e.g., H01, MBM) or Payer/Financial Class name
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            id="csv-file-input"
                        />
                        <label htmlFor="csv-file-input">
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<FontAwesomeIcon icon="file-upload" />}
                                sx={{ mt: 1 }}
                            >
                                Select CSV File
                            </Button>
                        </label>
                        {selectedFile && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Selected: {selectedFile.name}
                            </Typography>
                        )}
                    </Box>

                    {/* Parse Error */}
                    {parseError && (
                        <Alert severity="error">{parseError}</Alert>
                    )}

                    {/* Preview Table */}
                    {parsedData.length > 0 && !importResult && (
                        <Box>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="subtitle1">
                                    Preview ({parsedData.length} rows)
                                </Typography>
                                {validRows.length > 0 && (
                                    <Chip label={`${validRows.length} valid`} color="success" size="small" />
                                )}
                                {invalidRows.length > 0 && (
                                    <Chip label={`${invalidRows.length} invalid`} color="error" size="small" />
                                )}
                            </Stack>
                            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Line</TableCell>
                                            <TableCell>UPN</TableCell>
                                            <TableCell>Assignment Value</TableCell>
                                            <TableCell>Assignment Type</TableCell>
                                            <TableCell>Process Type</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsedData.slice(0, 50).map((row, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{ backgroundColor: row.isValid ? 'inherit' : 'error.light' }}
                                            >
                                                <TableCell>{row.lineNumber}</TableCell>
                                                <TableCell>{row.upn}</TableCell>
                                                <TableCell>{row.assignmentValue}</TableCell>
                                                <TableCell>{row.assignmentType}</TableCell>
                                                <TableCell>{row.processType}</TableCell>
                                                <TableCell>
                                                    {row.isValid ? (
                                                        <Chip label="Valid" color="success" size="small" />
                                                    ) : (
                                                        <Chip label={row.validationError} color="error" size="small" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {parsedData.length > 50 && (
                                <Typography variant="caption" color="text.secondary">
                                    Showing first 50 of {parsedData.length} rows
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Loading */}
                    {isLoading && (
                        <Box>
                            <Typography variant="body2" gutterBottom>Importing assignments...</Typography>
                            <LinearProgress />
                        </Box>
                    )}

                    {/* Import Results */}
                    {importResult && (
                        <Box>
                            <Alert severity={importResult.success ? "success" : "warning"} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2">
                                    Import {importResult.success ? 'Completed' : 'Completed with Errors'}
                                </Typography>
                                <Typography variant="body2">
                                    Total Processed: {importResult.totalProcessed} |
                                    Success: {importResult.successCount} |
                                    Errors: {importResult.errorCount}
                                </Typography>
                            </Alert>

                            {importResult.processedProfiles.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Processed Profiles:</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                        {importResult.processedProfiles.map((upn, index) => (
                                            <Chip key={index} label={upn} size="small" color="success" variant="outlined" />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {importResult.errors.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="error" gutterBottom>Errors:</Typography>
                                    <Paper sx={{ p: 2, maxHeight: 200, overflow: 'auto', backgroundColor: 'error.light' }}>
                                        {importResult.errors.map((error, index) => (
                                            <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                                • {error}
                                            </Typography>
                                        ))}
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit">
                    {importResult ? 'Close' : 'Cancel'}
                </Button>
                {!importResult && (
                    <Button
                        onClick={handleImport}
                        variant="contained"
                        color="primary"
                        disabled={validRows.length === 0 || isLoading}
                    >
                        Import {validRows.length} Assignments
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export { VobAssignmentBulkImport };
